import type { PublicPlayer } from '@cardengine/shared';
import { CardBack } from './PlayingCard.js';

interface PlayerSeatProps {
  player: PublicPlayer;
  isCurrent: boolean;
}

/** Beyond this the fan is drawn abbreviated with a counter. */
const MAX_VISIBLE_BACKS = 9;

export function PlayerSeat({ player, isCurrent }: PlayerSeatProps) {
  const visible = Math.min(player.handCount, MAX_VISIBLE_BACKS);

  const classes = [
    'seat',
    isCurrent ? 'seat--active' : '',
    !player.connected ? 'seat--away' : '',
    player.finishedPosition ? 'seat--done' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Mirror the player's own hand: a shallow fan rather than a squashed stack,
  // so an opponent's cards read as cards.
  const overlap = Math.min(0.58, 0.1 + visible * 0.04);

  return (
    <div className={classes} data-player-id={player.id}>
      <div className="seat__cards" aria-hidden="true">
        {Array.from({ length: visible }, (_, i) => {
          const offset = i - (visible - 1) / 2;
          const rotation = visible > 1 ? offset * Math.min(4, 20 / visible) : 0;
          return (
            <CardBack
              key={i}
              className="seat__card"
              style={{
                marginLeft: i === 0 ? 0 : `calc(var(--card-w) * -${overlap})`,
                zIndex: i,
                transform: `rotate(${rotation}deg) translateY(${Math.abs(offset) * 1.1}px)`,
              }}
            />
          );
        })}
        {player.handCount > MAX_VISIBLE_BACKS && (
          <span className="seat__overflow">+{player.handCount - MAX_VISIBLE_BACKS}</span>
        )}
      </div>

      <div className="seat__meta">
        <span className="seat__name">
          {player.name}
          {player.isBot && <span className="seat__tag">AI</span>}
          {!player.connected && !player.isBot && <span className="seat__tag seat__tag--away">away</span>}
        </span>
        <span className="seat__count">
          {player.handCount} {player.handCount === 1 ? 'card' : 'cards'}
        </span>
      </div>

      {player.handCount === 1 && player.declaredLastCard && (
        <span className="seat__flag seat__flag--declared">Last card!</span>
      )}

    </div>
  );
}
